/**
 * InvoiceDrawer Component
 * Create / Edit invoice form using portal pattern
 * BuilderBel Construction Project Management System
 */

import { yupResolver } from '@hookform/resolvers/yup';
import {
  DollarSign,
  FileText,
  Plus,
  Receipt,
  Trash2,
  User,
  X,
} from 'lucide-react';
import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { useToast } from '../../contexts/ToastContext';
import { useCurrentProjectId } from '../../hooks/useProjectRedux';
import { useGetVendorsQuery } from '../../services/vendorApi';
import { useCreateInvoiceMutation, useUpdateInvoiceMutation } from '../../services/invoicingApi';
import type {
  Invoice,
  InvoiceType,
  PaymentTerms,
  DiscountType,
  CreateInvoiceRequest,
  UpdateInvoiceRequest,
} from '../../types/invoice';
import {
  INVOICE_TYPE_LABELS,
  PAYMENT_TERMS_LABELS,
} from '../../types/invoice';
import {
  CurrencyInput,
  DatePicker,
  Dropdown,
  Input,
  NumberInput,
  SearchableDropdown,
  TextArea,
} from '../forms';

// ============================================================================
// TYPES
// ============================================================================

export interface InvoiceDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  invoice?: Invoice | null;
  mode: 'create' | 'edit' | 'view';
  onSuccess?: () => void;
}

interface LineItemFormData {
  description: string;
  quantity: number;
  unit: string;
  unitRate: number;
  costCode?: string;
  notes?: string;
}

interface InvoiceFormData {
  invoiceType: InvoiceType;
  title: string;
  clientId?: string;
  clientName: string;
  clientEmail: string;
  clientAddress: string;
  clientPhone: string;
  invoiceDate: string;
  dueDate: string;
  paymentTerms: PaymentTerms;
  taxRate: number;
  discount: number;
  discountType: DiscountType;
  currency: string;
  notes: string;
  terms: string;
  internalNotes: string;
  lineItems: LineItemFormData[];
}

// ============================================================================
// CONSTANTS
// ============================================================================

const INVOICE_TYPE_OPTIONS = (
  Object.entries(INVOICE_TYPE_LABELS) as [InvoiceType, string][]
).map(([value, label]) => ({ value, label }));

const PAYMENT_TERMS_OPTIONS = (
  Object.entries(PAYMENT_TERMS_LABELS) as [PaymentTerms, string][]
).map(([value, label]) => ({ value, label }));

const DISCOUNT_TYPE_OPTIONS = [
  { value: 'fixed', label: 'Fixed ($)' },
  { value: 'percentage', label: 'Percentage (%)' },
];

const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD – US Dollar' },
  { value: 'CAD', label: 'CAD – Canadian Dollar' },
  { value: 'EUR', label: 'EUR – Euro' },
  { value: 'GBP', label: 'GBP – British Pound' },
  { value: 'AUD', label: 'AUD – Australian Dollar' },
  { value: 'MXN', label: 'MXN – Mexican Peso' },
];

const DEFAULT_LINE_ITEM: LineItemFormData = {
  description: '',
  quantity: 1,
  unit: 'ea',
  unitRate: 0,
  costCode: '',
  notes: '',
};

// ============================================================================
// VALIDATION SCHEMA
// ============================================================================

const lineItemSchema = Yup.object({
  description: Yup.string().trim().required('Description is required').max(500),
  quantity: Yup.number().min(0, 'Must be 0 or more').required('Quantity is required'),
  unit: Yup.string().trim().required('Unit is required').max(50),
  unitRate: Yup.number().min(0, 'Must be 0 or more').required('Rate is required'),
  costCode: Yup.string().max(100).optional(),
  notes: Yup.string().max(500).optional(),
});

const invoiceSchema = Yup.object({
  invoiceType: Yup.string()
    .oneOf(['time_and_materials', 'fixed_price', 'service', 'progress', 'final'])
    .required('Invoice type is required'),
  title: Yup.string().max(200).optional(),
  clientId: Yup.string().optional(),
  clientName: Yup.string().trim().required('Client name is required').max(200),
  clientEmail: Yup.string().email('Invalid email address').max(255).optional(),
  clientAddress: Yup.string().max(500).optional(),
  clientPhone: Yup.string().max(50).optional(),
  invoiceDate: Yup.string().required('Invoice date is required'),
  dueDate: Yup.string().optional(),
  paymentTerms: Yup.string()
    .oneOf(['net_30', 'net_45', 'net_60', 'due_on_receipt', 'custom'])
    .required(),
  taxRate: Yup.number().min(0).max(100).optional(),
  discount: Yup.number().min(0).optional(),
  discountType: Yup.string().oneOf(['percentage', 'fixed']).optional(),
  currency: Yup.string().length(3).optional(),
  notes: Yup.string().max(2000).optional(),
  terms: Yup.string().max(2000).optional(),
  internalNotes: Yup.string().max(2000).optional(),
  lineItems: Yup.array().of(lineItemSchema).optional(),
});

// ============================================================================
// HELPERS
// ============================================================================

function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function todayStr(): string {
  return new Date().toISOString().split('T')[0];
}

// ============================================================================
// COMPONENT
// ============================================================================

export const InvoiceDrawer: React.FC<InvoiceDrawerProps> = ({
  isOpen,
  onClose,
  invoice,
  mode,
  onSuccess,
}) => {
  const projectId = useCurrentProjectId();
  const toast = useToast();
  const isViewMode = mode === 'view';

  const { data: vendorsResponse } = useGetVendorsQuery(
    { status: 'active', limit: 500 },
    { skip: isViewMode }
  );

  const [createInvoice, { isLoading: isCreating }] = useCreateInvoiceMutation();
  const [updateInvoice, { isLoading: isUpdating }] = useUpdateInvoiceMutation();
  const isSaving = isCreating || isUpdating;

  const vendorOptions = useMemo(() => {
    const vendorData = vendorsResponse?.data as { vendors?: { _id: string; name: string }[] } | undefined;
    const vendors = vendorData?.vendors || [];
    return vendors.map(v => ({ value: v._id, label: v.name }));
  }, [vendorsResponse]);

  const defaultValues: InvoiceFormData = useMemo(() => ({
    invoiceType: (invoice?.invoiceType as InvoiceType) || 'fixed_price',
    title: invoice?.title || '',
    clientId: invoice?.clientId || '',
    clientName: invoice?.clientName || '',
    clientEmail: invoice?.clientEmail || '',
    clientAddress: invoice?.clientAddress || '',
    clientPhone: invoice?.clientPhone || '',
    invoiceDate: invoice?.invoiceDate?.split('T')[0] || todayStr(),
    dueDate: invoice?.dueDate?.split('T')[0] || '',
    paymentTerms: (invoice?.paymentTerms as PaymentTerms) || 'net_30',
    taxRate: invoice?.taxRate ?? 0,
    discount: invoice?.discount ?? 0,
    discountType: (invoice?.discountType as DiscountType) || 'fixed',
    currency: invoice?.currency || 'USD',
    notes: invoice?.notes || '',
    terms: invoice?.terms || '',
    internalNotes: invoice?.internalNotes || '',
    lineItems:
      invoice?.lineItems?.map(item => ({
        description: item.description,
        quantity: item.quantity,
        unit: item.unit,
        unitRate: item.unitRate,
        costCode: item.costCode || '',
        notes: item.notes || '',
      })) || [{ ...DEFAULT_LINE_ITEM }],
  }), [invoice]);

  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm<InvoiceFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(invoiceSchema) as any,
    defaultValues,
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'lineItems' });

  // Reset form when invoice/mode changes
  useEffect(() => {
    reset(defaultValues);
  }, [invoice, mode, reset, defaultValues]);

  // Computed totals
  const watchedLineItems = watch('lineItems');
  const taxRate = watch('taxRate') || 0;
  const discount = watch('discount') || 0;
  const discountType = watch('discountType') || 'fixed';
  const currency = watch('currency') || 'USD';

  const computedTotals = useMemo(() => {
    const subtotal = (watchedLineItems || []).reduce(
      (sum, item) => sum + (item.quantity || 0) * (item.unitRate || 0),
      0
    );
    const taxAmount = (subtotal * (taxRate || 0)) / 100;
    const discountAmount =
      discountType === 'percentage'
        ? (subtotal * (discount || 0)) / 100
        : discount || 0;
    const total = Math.max(0, subtotal + taxAmount - discountAmount);
    return { subtotal, taxAmount, discountAmount, total };
  }, [watchedLineItems, taxRate, discount, discountType]);

  const handleClientSelect = (vendorId: string) => {
    setValue('clientId', vendorId);
    const vendor = (vendorsResponse?.data as unknown as { vendors?: { _id: string; name: string; email?: string; address?: string; phone?: string }[] })?.vendors?.find(
      (v: { _id: string }) => v._id === vendorId
    );
    if (vendor) {
      setValue('clientName', vendor.name);
      if (vendor.email) setValue('clientEmail', vendor.email);
      if (vendor.address) setValue('clientAddress', vendor.address);
      if (vendor.phone) setValue('clientPhone', vendor.phone);
    }
  };

  const onSubmit = async (data: InvoiceFormData) => {
    if (!projectId) {
      toast.error('No project selected');
      return;
    }

    try {
      const payload: CreateInvoiceRequest = {
        invoiceType: data.invoiceType,
        title: data.title || undefined,
        clientId: data.clientId || undefined,
        clientName: data.clientName,
        clientEmail: data.clientEmail || undefined,
        clientAddress: data.clientAddress || undefined,
        clientPhone: data.clientPhone || undefined,
        lineItems: (data.lineItems || []).map(item => ({
          description: item.description,
          quantity: item.quantity,
          unit: item.unit,
          unitRate: item.unitRate,
          costCode: item.costCode || undefined,
          notes: item.notes || undefined,
        })),
        invoiceDate: data.invoiceDate,
        dueDate: data.dueDate || undefined,
        taxRate: data.taxRate,
        discount: data.discount,
        discountType: data.discountType,
        currency: data.currency,
        paymentTerms: data.paymentTerms,
        notes: data.notes || undefined,
        terms: data.terms || undefined,
        internalNotes: data.internalNotes || undefined,
      };

      if (mode === 'create') {
        await createInvoice({ projectId, data: payload }).unwrap();
        toast.success('Invoice created successfully');
      } else {
        await updateInvoice({
          projectId,
          invoiceId: invoice!._id,
          data: payload as UpdateInvoiceRequest,
        }).unwrap();
        toast.success('Invoice updated successfully');
      }

      onSuccess?.();
      onClose();
    } catch {
      toast.error(mode === 'create' ? 'Failed to create invoice' : 'Failed to update invoice');
    }
  };

  if (!isOpen) return null;

  const title =
    mode === 'create' ? 'New Invoice' : mode === 'edit' ? 'Edit Invoice' : 'Invoice Details';

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed inset-6 z-[100] animate-in slide-in-from-right">
        <div className="bg-white rounded-2xl shadow-2xl h-full flex flex-col w-full">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-brand-50">
                <Receipt className="h-4 w-4 text-brand-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
                {invoice && (
                  <p className="text-xs text-gray-500">{invoice.invoiceNumber}</p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto px-8 py-6 space-y-8">
            <form id="invoice-form" onSubmit={handleSubmit(onSubmit)} noValidate>
              {/* ============================================================
                  SECTION 1: Invoice Details
              ============================================================ */}
              <section className="space-y-4">
                <div className="flex items-center space-x-2 mb-4">
                  <FileText className="h-4 w-4 text-gray-400" />
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                    Invoice Details
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Controller
                    name="invoiceType"
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        label="Invoice Type"
                        value={field.value}
                        onChange={field.onChange}
                        options={INVOICE_TYPE_OPTIONS}
                        disabled={isViewMode}
                        error={errors.invoiceType?.message}
                      />
                    )}
                  />

                  <Input
                    label="Title (optional)"
                    {...register('title')}
                    disabled={isViewMode}
                    error={errors.title?.message}
                    placeholder="e.g. Phase 1 Invoice"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Controller
                    name="invoiceDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        label="Invoice Date"
                        value={field.value}
                        onChange={field.onChange}
                        disabled={isViewMode}
                        error={errors.invoiceDate?.message}
                      />
                    )}
                  />

                  <Controller
                    name="dueDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        label="Due Date"
                        value={field.value}
                        onChange={field.onChange}
                        disabled={isViewMode}
                        error={errors.dueDate?.message}
                      />
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Controller
                    name="paymentTerms"
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        label="Payment Terms"
                        value={field.value}
                        onChange={field.onChange}
                        options={PAYMENT_TERMS_OPTIONS}
                        disabled={isViewMode}
                        error={errors.paymentTerms?.message}
                      />
                    )}
                  />

                  <Controller
                    name="currency"
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        label="Currency"
                        value={field.value}
                        onChange={field.onChange}
                        options={CURRENCY_OPTIONS}
                        disabled={isViewMode}
                        error={errors.currency?.message}
                      />
                    )}
                  />
                </div>
              </section>

              {/* ============================================================
                  SECTION 2: Client Info
              ============================================================ */}
              <section className="space-y-4 pt-6 border-t border-gray-100">
                <div className="flex items-center space-x-2 mb-4">
                  <User className="h-4 w-4 text-gray-400" />
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                    Client Information
                  </h3>
                </div>

                {!isViewMode && vendorOptions.length > 0 && (
                  <Controller
                    name="clientId"
                    control={control}
                    render={({ field }) => (
                      <SearchableDropdown
                        label="Select from Vendors (optional)"
                        value={field.value || ''}
                        onChange={val => {
                          field.onChange(val);
                          handleClientSelect(val);
                        }}
                        options={[{ value: '', label: 'Type manually...' }, ...vendorOptions]}
                        searchable
                        placeholder="Search vendors..."
                        disabled={isViewMode}
                      />
                    )}
                  />
                )}

                <Input
                  label="Client Name"
                  {...register('clientName')}
                  disabled={isViewMode}
                  error={errors.clientName?.message}
                  placeholder="Client or company name"
                />

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Client Email"
                    type="email"
                    {...register('clientEmail')}
                    disabled={isViewMode}
                    error={errors.clientEmail?.message}
                    placeholder="client@example.com"
                  />
                  <Input
                    label="Client Phone"
                    {...register('clientPhone')}
                    disabled={isViewMode}
                    error={errors.clientPhone?.message}
                    placeholder="+1 (555) 000-0000"
                  />
                </div>

                <Controller
                  name="clientAddress"
                  control={control}
                  render={({ field }) => (
                    <TextArea
                      label="Client Address"
                      value={field.value}
                      onChange={field.onChange}
                      disabled={isViewMode}
                      error={errors.clientAddress?.message}
                      placeholder="Street, City, State ZIP"
                      rows={2}
                    />
                  )}
                />
              </section>

              {/* ============================================================
                  SECTION 3: Line Items
              ============================================================ */}
              <section className="space-y-3 pt-6 border-t border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <DollarSign className="h-4 w-4 text-gray-400" />
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                      Line Items
                    </h3>
                  </div>
                  {!isViewMode && (
                    <button
                      type="button"
                      onClick={() => append({ ...DEFAULT_LINE_ITEM })}
                      className="flex items-center space-x-1 text-xs font-medium text-brand-600 hover:text-brand-700 transition-colors"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      <span>Add Item</span>
                    </button>
                  )}
                </div>

                {/* Header row */}
                <div className="grid grid-cols-12 gap-2 px-1 pb-1 border-b border-gray-200">
                  <div className="col-span-4 text-xs font-medium text-gray-500 uppercase">Description</div>
                  <div className="col-span-2 text-xs font-medium text-gray-500 uppercase">Qty</div>
                  <div className="col-span-1 text-xs font-medium text-gray-500 uppercase">Unit</div>
                  <div className="col-span-2 text-xs font-medium text-gray-500 uppercase">Rate</div>
                  <div className="col-span-2 text-xs font-medium text-gray-500 uppercase">Amount</div>
                  {!isViewMode && <div className="col-span-1" />}
                </div>

                {fields.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-4">
                    No line items. Click "Add Item" to get started.
                  </p>
                )}

                {fields.map((field, index) => {
                  const qty = watchedLineItems?.[index]?.quantity || 0;
                  const rate = watchedLineItems?.[index]?.unitRate || 0;
                  const amount = qty * rate;

                  return (
                    <div
                      key={field.id}
                      className="grid grid-cols-12 gap-2 items-start py-2 border-b border-gray-50"
                    >
                      <div className="col-span-4">
                        <Input
                          {...register(`lineItems.${index}.description`)}
                          disabled={isViewMode}
                          placeholder="Description"
                          error={errors.lineItems?.[index]?.description?.message}
                        />
                      </div>
                      <div className="col-span-2">
                        <Controller
                          name={`lineItems.${index}.quantity`}
                          control={control}
                          render={({ field: f }) => (
                            <NumberInput
                              value={f.value}
                              onChange={f.onChange}
                              disabled={isViewMode}
                              placeholder="1"
                              min={0}
                              step={0.01}
                              error={errors.lineItems?.[index]?.quantity?.message}
                            />
                          )}
                        />
                      </div>
                      <div className="col-span-1">
                        <Input
                          {...register(`lineItems.${index}.unit`)}
                          disabled={isViewMode}
                          placeholder="ea"
                          error={errors.lineItems?.[index]?.unit?.message}
                        />
                      </div>
                      <div className="col-span-2">
                        <Controller
                          name={`lineItems.${index}.unitRate`}
                          control={control}
                          render={({ field: f }) => (
                            <CurrencyInput
                              value={f.value}
                              onChange={f.onChange}
                              disabled={isViewMode}
                              placeholder="0.00"
                              error={errors.lineItems?.[index]?.unitRate?.message}
                            />
                          )}
                        />
                      </div>
                      <div className="col-span-2 flex items-center h-10 px-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium text-gray-700">
                          {formatCurrency(amount, currency)}
                        </span>
                      </div>
                      {!isViewMode && (
                        <div className="col-span-1 flex items-center justify-center h-10">
                          <button
                            type="button"
                            onClick={() => remove(index)}
                            disabled={fields.length === 1}
                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* ============================================================
                    SECTION 4: Summary
                ============================================================ */}
                <div className="pt-4 space-y-2">
                  <div className="flex justify-end">
                    <div className="w-72 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Subtotal</span>
                        <span className="font-medium text-gray-900">
                          {formatCurrency(computedTotals.subtotal, currency)}
                        </span>
                      </div>

                      {/* Tax */}
                      <div className="flex items-center justify-between gap-4">
                        <span className="text-sm text-gray-500 shrink-0">Tax Rate (%)</span>
                        {isViewMode ? (
                          <span className="text-sm font-medium text-gray-900">
                            {taxRate}% = {formatCurrency(computedTotals.taxAmount, currency)}
                          </span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Controller
                              name="taxRate"
                              control={control}
                              render={({ field: f }) => (
                                <NumberInput
                                  value={f.value}
                                  onChange={f.onChange}
                                  min={0}
                                  max={100}
                                  step={0.01}
                                  placeholder="0"
                                  className="w-20"
                                />
                              )}
                            />
                            <span className="text-sm text-gray-500">
                              = {formatCurrency(computedTotals.taxAmount, currency)}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Discount */}
                      <div className="flex items-center justify-between gap-4">
                        <span className="text-sm text-gray-500 shrink-0">Discount</span>
                        {isViewMode ? (
                          <span className="text-sm font-medium text-gray-900">
                            -{formatCurrency(computedTotals.discountAmount, currency)}
                          </span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Controller
                              name="discountType"
                              control={control}
                              render={({ field: f }) => (
                                <Dropdown
                                  value={f.value}
                                  onChange={f.onChange}
                                  options={DISCOUNT_TYPE_OPTIONS}
                                />
                              )}
                            />
                            <Controller
                              name="discount"
                              control={control}
                              render={({ field: f }) => (
                                <NumberInput
                                  value={f.value}
                                  onChange={f.onChange}
                                  min={0}
                                  step={0.01}
                                  placeholder="0"
                                  className="w-20"
                                />
                              )}
                            />
                          </div>
                        )}
                      </div>

                      {computedTotals.discountAmount > 0 && (
                        <div className="flex justify-between text-sm text-green-600">
                          <span>Discount applied</span>
                          <span>-{formatCurrency(computedTotals.discountAmount, currency)}</span>
                        </div>
                      )}

                      <div className="border-t border-gray-200 pt-2 flex justify-between">
                        <span className="text-base font-semibold text-gray-900">Total</span>
                        <span className="text-base font-bold text-brand-600">
                          {formatCurrency(computedTotals.total, currency)}
                        </span>
                      </div>

                      {invoice && invoice.amountPaid > 0 && (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-500">Amount Paid</span>
                            <span className="font-medium text-green-600">
                              {formatCurrency(invoice.amountPaid, currency)}
                            </span>
                          </div>
                          <div className="border-t border-gray-200 pt-2 flex justify-between">
                            <span className="text-sm font-semibold text-gray-900">Balance Due</span>
                            <span className="text-sm font-bold text-red-600">
                              {formatCurrency(invoice.balanceDue, currency)}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </section>

              {/* ============================================================
                  SECTION 5: Notes & Terms
              ============================================================ */}
              <section className="space-y-4 pt-6 border-t border-gray-100">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                  Notes & Terms
                </h3>

                <Controller
                  name="notes"
                  control={control}
                  render={({ field }) => (
                    <TextArea
                      label="Client Notes"
                      value={field.value}
                      onChange={field.onChange}
                      disabled={isViewMode}
                      error={errors.notes?.message}
                      placeholder="Notes visible to the client on the invoice"
                      rows={3}
                    />
                  )}
                />

                <Controller
                  name="terms"
                  control={control}
                  render={({ field }) => (
                    <TextArea
                      label="Payment Terms"
                      value={field.value}
                      onChange={field.onChange}
                      disabled={isViewMode}
                      error={errors.terms?.message}
                      placeholder="e.g. Payment is due within 30 days of invoice date..."
                      rows={2}
                    />
                  )}
                />

                <Controller
                  name="internalNotes"
                  control={control}
                  render={({ field }) => (
                    <TextArea
                      label="Internal Notes"
                      value={field.value}
                      onChange={field.onChange}
                      disabled={isViewMode}
                      error={errors.internalNotes?.message}
                      placeholder="Internal notes (not shown to client)"
                      rows={2}
                    />
                  )}
                />
              </section>
            </form>
          </div>

          {/* Footer */}
          {!isViewMode && (
            <div className="border-t border-gray-200 px-8 py-6 bg-gray-50 rounded-b-2xl flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="invoice-form"
                disabled={isSaving}
                className="px-5 py-2.5 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSaving
                  ? mode === 'create'
                    ? 'Creating...'
                    : 'Saving...'
                  : mode === 'create'
                  ? 'Create Invoice'
                  : 'Save Changes'}
              </button>
            </div>
          )}

          {isViewMode && (
            <div className="border-t border-gray-200 px-8 py-6 bg-gray-50 rounded-b-2xl flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  );
};

export default InvoiceDrawer;
